import { useEffect } from "react"; // Import useEffect
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreateInvoicingDto } from "@/lib/api";
import { InvoicingDeliveryType, Months, Invoicing } from "@/lib/types"; // Import Invoicing

const formSchema = z.object( {
  invoicingName: z.string().min( 2, "O nome deve ter pelo menos 2 caracteres." ),
  deliveryTypeId: z.coerce.number().min( 1, "ID do tipo de entrega é obrigatório." ),
  invoicingQtd: z.coerce.number().min( 1, "A quantidade deve ser maior que zero." ),
  currentMonth: z.coerce.number().min( 1, "Mês inválido." ).max( 12, "Mês inválido." ),
  currentYear: z.coerce.number().min( new Date().getFullYear(), "Ano não pode ser no passado." ),
} );

type InvoicingFormValues = z.infer<typeof formSchema>;

interface InvoicingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: ( data: CreateInvoicingDto ) => void;
  isLoading: boolean;
  initialData?: Invoicing; // New prop for editing
}

export function InvoicingFormModal( {
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  initialData, // Destructure initialData
}: InvoicingFormModalProps ) {
  const isEditing = !!initialData; // Determine if in editing mode

  const form = useForm<InvoicingFormValues>( {
    resolver: zodResolver( formSchema ),
    defaultValues: {
      invoicingName: "",
      deliveryTypeId: InvoicingDeliveryType.Anual,
      invoicingQtd: 1,
      currentMonth: new Date().getMonth() + 1,
      currentYear: new Date().getFullYear(),
    },
  } );

  // Populate form with initialData when in editing mode
  useEffect( () => {
    if ( isEditing && initialData ) {
      form.reset( {
        invoicingName: initialData.invoicingName,
        deliveryTypeId: initialData.deliveryTypeId,
        invoicingQtd: initialData.invoicingQtd,
        currentMonth: initialData.currentMonth || new Date().getMonth() + 1,
        currentYear: initialData.currentYear || new Date().getFullYear(),
      } );
    } else {
      // Reset form when not editing or modal is closed
      form.reset( {
        invoicingName: "",
        deliveryTypeId: InvoicingDeliveryType.Anual,
        invoicingQtd: 1,
        currentMonth: new Date().getMonth() + 1,
        currentYear: new Date().getFullYear(),
      } );
    }
  }, [ isEditing, initialData, form ] );

  const handleFormSubmit = ( data: InvoicingFormValues ) => {
    onSubmit( data );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar Faturamento" : "Adicionar Faturamento"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit( handleFormSubmit )} className="space-y-4">
            <FormField
              control={form.control}
              name="invoicingName"
              render={( { field } ) => (
                <FormItem>
                  <FormLabel>Mês de referência</FormLabel>
                  <FormControl>
                    <Input placeholder={`Ex: Janeiro ${ new Date().getFullYear() }`} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="deliveryTypeId"
              render={( { field } ) => (
                <FormItem>
                  <FormLabel>Tipo de Entrega</FormLabel>
                  <Select
                    onValueChange={( value ) => field.onChange( Number( value ) )}
                    value={String( field.value )}
                    defaultValue={String( field.value )}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo de entrega" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries( InvoicingDeliveryType )
                        .filter( ( [ key, value ] ) => typeof value === "number" )
                        .map( ( [ key, value ] ) => (
                          <SelectItem key={value} value={String( value )}>
                            {key}
                          </SelectItem>
                        ) )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="invoicingQtd"
              render={( { field } ) => (
                <FormItem>
                  <FormLabel>Quantidade</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="currentMonth"
                render={( { field } ) => (
                  <FormItem>
                    <FormLabel>Mês</FormLabel>
                    <Select
                      onValueChange={( value ) => field.onChange( Number( value ) )}
                      value={String( field.value )}
                      defaultValue={String( field.value )}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o mês" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries( Months )
                          .filter( ( [ key, value ] ) => typeof value === "number" )
                          .map( ( [ key, value ] ) => (
                            <SelectItem key={value} value={String( value )}>
                              {key}
                            </SelectItem>
                          ) )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currentYear"
                render={( { field } ) => (
                  <FormItem>
                    <FormLabel>Ano</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? ( isEditing ? "Salvando..." : "Adicionando..." ) : ( isEditing ? "Salvar Alterações" : "Adicionar Faturamento" )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}